'use client';

import { Button, ButtonType } from '@/components/ui/button';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { MdClose, MdKeyboardArrowDown } from 'react-icons/md';

import type { SiteNavNodeView, SiteNavZonesView } from '@/components/site-page/SitePage.types';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Link } from '@/lib/app-router';
import { SiteHeaderNavLink, SiteHeaderNodeContent } from './SiteHeaderParts';
import {
  drawerBodyClasses,
  drawerClasses,
  drawerCloseClasses,
  drawerFootClasses,
  drawerHeadClasses,
  drawerItemClasses,
  drawerScrimClasses,
  drawerSessionChipClasses,
  drawerSignInClasses,
  drawerZoneClasses,
  drawerZoneHeadingClasses,
  logoLinkClasses,
} from './SiteHeader.style';

interface SiteHeaderDrawerProps {
  id: string;
  logo: ReactNode;
  logoHref: string;
  zones: SiteNavZonesView;
  isSignedIn: boolean;
  showLogin: boolean;
  loginHref: string;
  /** The session chip's long form, from the indicator's own copy. Set only during a live session. */
  sessionText?: string;
  onSessionClick?: () => void;
  /** `returnFocus` true puts focus back on the menu button. */
  onClose: (returnFocus: boolean) => void;
}

/**
 * The phone menu: a left drawer with the site navigation (PB-9033).
 *
 * ## Invariants:
 * - Rendered only while open and only below the md breakpoint (the parent decides with
 *   `useMediaQuery`); inline, never portaled, so it can never co-render with a desktop variant.
 * - A live session pins the session chip at the top and the CENTER zone is NOT rendered (`zones`
 *   arrives with CENTER already unused by the caller). Items are text only.
 * - Focus is trapped (`useFocusTrap`, close button first). Escape, a scrim tap or the close button
 *   close it and return focus to the menu button; body scroll is locked while open and the prior
 *   value restored on close or unmount.
 * - Makes no request: it renders the props SiteHeader already has.
 */
export function SiteHeaderDrawer({
  id,
  logo,
  logoHref,
  zones,
  isSignedIn,
  showLogin,
  loginHref,
  sessionText,
  onSessionClick,
  onClose,
}: SiteHeaderDrawerProps) {
  const t = useTranslations('SiteHeader');
  const tSite = useTranslations('SitePage');
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const baseId = useId();

  useFocusTrap(drawerRef, true, { initialFocusRef: closeRef });

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose(true);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const zoneBlock = (
    zone: 'left' | 'center' | 'right',
    nodes: SiteNavNodeView[],
    heading?: string
  ) =>
    nodes.length > 0 ? (
      <nav
        className={drawerZoneClasses}
        aria-label={heading ?? t(zone === 'left' ? 'leftNav' : 'rightNav')}
        data-cy={`site-header-drawer-zone-${zone}`}
      >
        {heading && <h2 className={drawerZoneHeadingClasses}>{heading}</h2>}
        <DrawerNodes
          nodes={nodes}
          level={1}
          baseId={`${baseId}-${zone}`}
          onDone={() => onClose(false)}
        />
      </nav>
    ) : null;

  return (
    <>
      <div
        className={drawerScrimClasses}
        data-cy="site-header-drawer-scrim"
        aria-hidden="true"
        onClick={() => onClose(true)}
      />
      <div
        ref={drawerRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={t('siteMenu')}
        className={drawerClasses}
        data-cy="site-header-drawer"
      >
        <div className={drawerHeadClasses}>
          <Link
            href={logoHref}
            className={logoLinkClasses}
            data-cy="site-header-drawer-logo"
            onClick={() => onClose(false)}
          >
            {logo}
          </Link>
          <Button
            buttonType={ButtonType.text}
            ref={closeRef}
            className={drawerCloseClasses}
            aria-label={tSite('closeMenu')}
            data-cy="site-header-drawer-close"
            onClick={() => onClose(true)}
          >
            <MdClose size={24} aria-hidden="true" />
          </Button>
        </div>
        <div className={drawerBodyClasses}>
          {sessionText && (
            <Button
              buttonType={ButtonType.text}
              className={drawerSessionChipClasses}
              data-cy="site-header-drawer-session-chip"
              onClick={() => {
                onClose(false);
                onSessionClick?.();
              }}
            >
              {sessionText}
            </Button>
          )}
          {zoneBlock('left', zones.left)}
          {!sessionText && zoneBlock('center', zones.center, t('quickActions'))}
          {zoneBlock('right', zones.right)}
          {!isSignedIn && showLogin && (
            <div className={drawerFootClasses}>
              <Link
                href={loginHref}
                className={drawerSignInClasses}
                data-cy="site-header-drawer-sign-in"
                onClick={() => onClose(false)}
              >
                {tSite('signIn')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

interface DrawerNodesProps {
  nodes: SiteNavNodeView[];
  level: 1 | 2 | 3;
  baseId: string;
  onDone: () => void;
}

function DrawerNodes({ nodes, level, baseId, onDone }: DrawerNodesProps) {
  return (
    <>
      {nodes.map((node) =>
        node.kind === 'GROUP' ? (
          <DrawerGroup key={node.id} node={node} level={level} baseId={baseId} onDone={onDone} />
        ) : (
          <DrawerItem key={node.id} node={node} level={level} onDone={onDone} />
        )
      )}
    </>
  );
}

function DrawerItem({
  node,
  level,
  onDone,
}: {
  node: SiteNavNodeView;
  level: 1 | 2 | 3;
  onDone: () => void;
}) {
  const content = <DrawerContent node={node} />;
  if (node.link) {
    return (
      <SiteHeaderNavLink
        link={node.link}
        className={drawerItemClasses({ level })}
        data-cy="site-header-drawer-item"
        onClick={onDone}
      >
        {content}
      </SiteHeaderNavLink>
    );
  }
  return (
    <Button
      buttonType={ButtonType.text}
      className={drawerItemClasses({ level })}
      data-cy="site-header-drawer-item"
      onClick={() => {
        onDone();
        node.onActivate?.();
      }}
    >
      {content}
    </Button>
  );
}

/** Icon beside the always-visible text: the label, else the accessible name. */
function DrawerContent({ node }: { node: SiteNavNodeView }) {
  return (
    <SiteHeaderNodeContent
      node={{ ...node, label: node.label ?? node.a11yLabel }}
      showLabel
      iconCy="site-header-node-icon"
    />
  );
}

function DrawerGroup({
  node,
  level,
  baseId,
  onDone,
}: {
  node: SiteNavNodeView;
  level: 1 | 2 | 3;
  baseId: string;
  onDone: () => void;
}) {
  const t = useTranslations('Navigation');
  const [open, setOpen] = useState(false);
  const panelId = `${baseId}-${node.id}`;
  const childLevel = (level === 1 ? 2 : 3) as 2 | 3;
  return (
    <div>
      <Button
        buttonType={ButtonType.text}
        className={drawerItemClasses({ level })}
        aria-expanded={open}
        aria-controls={panelId}
        data-cy="site-header-drawer-group-toggle"
        onClick={() => setOpen((current) => !current)}
      >
        <DrawerContent node={node} />
        <MdKeyboardArrowDown
          size={20}
          aria-hidden="true"
          className={open ? 'tw:ml-auto tw:rotate-180' : 'tw:ml-auto'}
        />
      </Button>
      <div id={panelId} hidden={!open}>
        {open && node.overview && (
          <SiteHeaderNavLink
            link={node.overview}
            className={drawerItemClasses({ level: childLevel })}
            data-cy="site-header-drawer-item"
            onClick={onDone}
          >
            {t('overview')}
          </SiteHeaderNavLink>
        )}
        {open && (
          <DrawerNodes nodes={node.children} level={childLevel} baseId={panelId} onDone={onDone} />
        )}
      </div>
    </div>
  );
}
